// PlatformLLMKeysPanel — Super Admin only.
//
// Direct-to-provider platform keys. When set, the platform calls Anthropic /
// OpenAI / Google directly at 1× raw cost. The 3× creator markup on top is
// pure margin. When a provider slot is empty, calls resolved to that
// provider fail cleanly (rather than silently routing to any middleman) so
// the missing configuration is loud and obvious.

import { useEffect, useState } from "react";
import { Key, Eye, CheckCircle, WarningCircle, ArrowSquareOut, X, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROVIDERS = [
  {
    id: "anthropic",
    label: "Anthropic (Claude)",
    short: "Claude Sonnet · Haiku · Opus",
    consoleUrl: "https://console.anthropic.com/settings/keys",
    keyHint: "starts with sk-ant-...",
    walkthrough: [
      "Sign in at console.anthropic.com (or create a free account).",
      "Open Settings → API Keys → Create Key. Name it 'MySilo platform'.",
      "Copy the key (you'll only see it once) and paste it below.",
      "Before saving: top up Anthropic with at least $25 under Settings → Billing, and enable auto-reload. A failed top-up means every Claude call on the platform stops.",
      "After 7 days of clean usage Anthropic auto-promotes you to Tier 2 (higher rate limits). Apply early if you can.",
    ],
  },
  {
    id: "openai",
    label: "OpenAI (GPT)",
    short: "GPT-5.2 · GPT-4o · Whisper · Sora",
    consoleUrl: "https://platform.openai.com/api-keys",
    keyHint: "starts with sk-...",
    walkthrough: [
      "Sign in at platform.openai.com.",
      "Open API keys → Create new secret key. Name it 'MySilo platform'.",
      "Copy the key and paste it below.",
      "Top up OpenAI with at least $25 under Settings → Billing, enable auto-reload.",
      "OpenAI tiers up automatically based on spend + age — Tier 2 unlocks at $50 lifetime spend + 7 days.",
    ],
  },
  {
    id: "gemini_platform",
    label: "Google Gemini (platform)",
    short: "Fallback for Gemini calls when a creator has no BYOK set",
    consoleUrl: "https://aistudio.google.com/app/apikey",
    keyHint: "starts with AIzaSy...",
    walkthrough: [
      "Sign in at aistudio.google.com.",
      "Open Get API key → Create API key. Pick or create a Google Cloud project.",
      "Copy the key and paste it below.",
      "Gemini's free tier is generous — you usually won't need paid billing right away. Add a card if you want the higher Tier 1 rate limits.",
    ],
  },
];

function WalkthroughModal({ provider, onClose }) {
  if (!provider) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}
      data-testid={`platform-llm-walkthrough-${provider.id}`}>
      <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#7A5200] flex items-center gap-1.5">
              <Key size={12} weight="duotone" /> How to get your key
            </div>
            <h3 className="font-serif-display text-xl text-[#1C1B1A] mt-1">{provider.label}</h3>
            <p className="text-xs text-[#6B6A68] mt-1">{provider.short}</p>
          </div>
          <button onClick={onClose} className="text-[#6B6A68] hover:text-[#1C1B1A] p-1"
            data-testid="platform-llm-walkthrough-close">
            <X size={16} />
          </button>
        </div>

        <ol className="space-y-2 mb-4">
          {provider.walkthrough.map((step, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#1C1B1A]">
              <span className="text-[10px] font-medium text-[#7A5200] bg-[#FBE9E1] rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>

        <a href={provider.consoleUrl} target="_blank" rel="noopener noreferrer"
          data-testid={`platform-llm-walkthrough-open-${provider.id}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#4A5D4E] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#1C1B1A]">
          <ArrowSquareOut size={12} /> Open {provider.label} console
        </a>
      </div>
    </div>
  );
}

function ProviderRow({ provider, isSet, onSave, onClear, busy }) {
  const [showInput, setShowInput] = useState(false);
  const [draft, setDraft] = useState("");
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const save = async () => {
    if (!draft.trim()) return toast.error("Paste a key first.");
    await onSave(draft.trim());
    setDraft("");
    setShowInput(false);
  };

  const testKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/platform-llm-keys/test", { provider: provider.id });
      setTestResult(r.data);
      if (r.data.ok) toast.success(`${provider.label} key works ✓`);
      else toast.error(r.data.error || "Test failed");
    } catch (e) {
      const err = e.response?.data?.detail || e.response?.data?.error || "Test failed";
      setTestResult({ ok: false, error: err });
      toast.error(err);
    } finally { setTesting(false); }
  };

  return (
    <>
      <div className={`bg-white border hairline rounded-lg p-4 ${isSet ? "border-l-4 border-l-[#4A5D4E]" : ""}`}
        data-testid={`platform-llm-row-${provider.id}`}>
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${isSet ? "bg-[#E8EBE5] text-[#4A5D4E]" : "bg-[#F0F1EE] text-[#6B6A68]"}`}>
            {isSet ? <CheckCircle size={18} weight="duotone" /> : <Key size={18} weight="duotone" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-medium text-sm text-[#1C1B1A]">{provider.label}</h4>
              {isSet ? (
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4A5D4E] bg-[#E8EBE5] px-2 py-0.5 rounded">Active</span>
              ) : (
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#7A5200] bg-[#FBE9E1] px-2 py-0.5 rounded">Not set</span>
              )}
            </div>
            <p className="text-xs text-[#6B6A68] mt-0.5">{provider.short}</p>

            {!showInput && (
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button type="button" onClick={() => setShowInput(true)}
                  data-testid={`platform-llm-${provider.id}-add-btn`}
                  className="px-3 py-1.5 rounded-md border hairline text-xs uppercase tracking-[0.15em] text-[#4A5D4E] hover:bg-[#F0F1EE]">
                  {isSet ? "Rotate key" : "Add key"}
                </button>
                <button type="button" onClick={() => setShowWalkthrough(true)}
                  data-testid={`platform-llm-${provider.id}-help-btn`}
                  className="px-2.5 py-1.5 rounded-md text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#F0F1EE] inline-flex items-center gap-1 text-xs">
                  <Eye size={12} weight="duotone" /> How to get one
                </button>
                {isSet && (
                  <button type="button" onClick={testKey} disabled={busy || testing}
                    data-testid={`platform-llm-${provider.id}-test-btn`}
                    className="px-2.5 py-1.5 rounded-md text-[#4A5D4E] hover:bg-[#F0F1EE] inline-flex items-center gap-1 text-xs disabled:opacity-50">
                    {testing ? "Testing…" : "Test ping"}
                  </button>
                )}
                {isSet && (
                  <button type="button" onClick={onClear} disabled={busy}
                    data-testid={`platform-llm-${provider.id}-clear-btn`}
                    className="px-2.5 py-1.5 rounded-md text-[#4A5D4E] hover:bg-[#E8EBE5] text-xs disabled:opacity-50">
                    Clear
                  </button>
                )}
              </div>
            )}
            {testResult && (
              <div className={`mt-2 text-[11px] p-2 rounded ${testResult.ok ? "bg-[#E8EBE5] text-[#2F6B3A]" : "bg-[#FDF1ED] text-[#7A2A20]"}`}
                data-testid={`platform-llm-${provider.id}-test-result`}>
                {testResult.ok ? `Live · ${testResult.model || "ok"}` : `Failed · ${testResult.error}`}
              </div>
            )}

            {showInput && (
              <div className="mt-3 space-y-2">
                <input
                  type="password"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={`Paste key — ${provider.keyHint}`}
                  data-testid={`platform-llm-${provider.id}-input`}
                  className="w-full px-3 py-2 border hairline rounded-md text-sm font-mono"
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button type="button" onClick={save} disabled={busy}
                    data-testid={`platform-llm-${provider.id}-save-btn`}
                    className="px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#1C1B1A] disabled:opacity-50 inline-flex items-center gap-1">
                    <FloppyDisk size={12} /> Save
                  </button>
                  <button type="button" onClick={() => { setDraft(""); setShowInput(false); }}
                    data-testid={`platform-llm-${provider.id}-cancel-btn`}
                    className="px-3 py-1.5 rounded-md border hairline text-xs uppercase tracking-[0.15em] text-[#6B6A68] hover:bg-[#F0F1EE]">
                    Cancel
                  </button>
                </div>
                <p className="text-[10px] text-[#6B6A68]">
                  Stored encrypted. Never echoed back to the UI. Cleared by emptying this field and saving.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
      {showWalkthrough && <WalkthroughModal provider={provider} onClose={() => setShowWalkthrough(false)} />}
    </>
  );
}

export default function PlatformLLMKeysPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/platform-llm-keys");
      setStatus(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load platform LLM key status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (provider, apiKey) => {
    setBusy(true);
    try {
      const r = await api.patch("/admin/platform-llm-keys", { provider, api_key: apiKey });
      setStatus(r.data);
      toast.success(`${provider} key saved · platform will route ${provider === "anthropic" ? "Claude" : provider === "openai" ? "GPT" : "Gemini"} calls directly.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save key.");
    } finally {
      setBusy(false);
    }
  };

  const clear = async (provider) => {
    if (!window.confirm(`Clear the ${provider} key? Calls resolved to that provider will fail with a "missing key" error until you save a new one.`)) return;
    setBusy(true);
    try {
      const r = await api.patch("/admin/platform-llm-keys", { provider, api_key: "" });
      setStatus(r.data);
      toast.success("Key cleared.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not clear key.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="text-sm text-[#6B6A68] py-6" data-testid="platform-llm-loading">Loading platform LLM keys…</div>;

  const allSet = !!(status?.anthropic_set && status?.openai_set && status?.gemini_platform_set);
  const anySet = !!(status?.anthropic_set || status?.openai_set || status?.gemini_platform_set);

  return (
    <section className="mt-0" data-testid="platform-llm-keys-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${allSet ? "bg-[#E8EBE5] text-[#4A5D4E]" : "bg-[#FBE9E1] text-[#7A5200]"}`}>
          <Key size={18} weight="duotone" />
        </div>
        <div className="flex-1">
          <h2 className="font-serif-display text-2xl leading-none">Platform LLM Keys</h2>
          <p className="text-sm text-[#6B6A68] mt-1 max-w-3xl leading-relaxed">
            Direct API keys for Anthropic, OpenAI, and Google. When set, the platform calls these providers directly at <strong>1× raw cost</strong>. The 3× creator markup stays on top of that — pure platform margin. A missing slot means calls resolved to that provider fail cleanly with a "no key" error, never through any middleman.
          </p>
        </div>
      </div>

      {/* Status banner — Iter160w (Randy pt-siloart-seagreen-encased-sweep)
          Sea-green (#E8F0E9), amber (#FBE9E1), and pink (#FDF1ED) tones
          aren't in the CSS whitetext/blacktext exclusion chain, so their
          inner text bleeds to white under Silo Art V2. `.siloart-encased`
          keeps all descendants at their native dark color. */}
      <div className={`mb-4 p-3 rounded-md border siloart-encased text-sm ${allSet ? "border-[#4A5D4E] bg-[#E8EBE5] text-[#1C1B1A]" : anySet ? "border-[#7A5200] bg-[#FBE9E1] text-[#7A5200]" : "border-[#C16E5A] bg-[#FDF1ED] text-[#7A2A20]"}`}
        data-testid="platform-llm-status-banner">
        {allSet ? (
          <span className="flex items-center gap-2"><CheckCircle size={14} weight="duotone" /> <strong>All three providers wired.</strong> Every LLM call routes directly to the provider you keyed in — no middleman, no proxy.</span>
        ) : anySet ? (
          <span className="flex items-center gap-2"><WarningCircle size={14} weight="duotone" /> <strong>Partial coverage.</strong> Calls resolved to an unkeyed provider will fail with a "no key" error. Add the remaining keys to close the gap.</span>
        ) : (
          <span className="flex items-center gap-2"><WarningCircle size={14} weight="duotone" /> <strong>No platform keys set yet.</strong> Every non-BYOK call will fail with a "no key" error until you add Anthropic, OpenAI, and Google keys here.</span>
        )}
      </div>

      <div className="space-y-3">
        {PROVIDERS.map((p) => (
          <ProviderRow
            key={p.id}
            provider={p}
            isSet={!!status?.[`${p.id}_set`]}
            busy={busy}
            onSave={(key) => save(p.id, key)}
            onClear={() => clear(p.id)}
          />
        ))}
      </div>

      <div className="mt-4 text-[11px] text-[#6B6A68] leading-relaxed">
        <strong>How the swap works:</strong> the moment you save a real provider key here, every call site resolving to that provider's models routes directly to them via the LiteLLM library. No call sites or other config need to change. Token usage and cost reporting work exactly the same; the only difference is what the underlying provider invoice looks like at the end of the month.
      </div>
    </section>
  );
}
